import type { AdminSection } from '@/components/layout/admin/Sidebar';

/** Sidebar category order (top → bottom). */
export const ADMIN_NAV_GROUPS = [
  'General',
  'Learning',
  'Growth',
  'Commerce',
  'Store',
  'Orders',
  'User management',
  'Support',
  'Finance',
  'Marketing',
  'System',
  'Account',
] as const;

export type AdminNavGroup = (typeof ADMIN_NAV_GROUPS)[number];

/** Admin-facing sections (qbank + assignments live on the instructor sidebar only). */
export const ALL_ADMIN_SECTIONS: AdminSection[] = [
  { key: 'overview', label: 'Overview', group: 'General' },
  { key: 'reports', label: 'Reports', group: 'General' },

  { key: 'courses', label: 'Courses', group: 'Store' },
  { key: 'bundles', label: 'Bundles', group: 'Store' },
  { key: 'books', label: 'Books', group: 'Store' },
  { key: 'coupons', label: 'Coupons', group: 'Store' },
  { key: 'freeresources', label: 'Free resources', group: 'Store' },

  { key: 'orders', label: 'Course orders', group: 'Orders' },
  { key: 'bookorders', label: 'Book orders', group: 'Orders' },

  { key: 'students', label: 'Students', group: 'User management' },
  { key: 'instructors', label: 'Instructors', group: 'User management' },
  { key: 'affiliates', label: 'Affiliates', group: 'User management' },
  { key: 'ordermanagers', label: 'Order managers', group: 'User management' },

  { key: 'tickets', label: 'Tickets', group: 'Support' },
  { key: 'reviews', label: 'Reviews', group: 'Support' },
  { key: 'leads', label: 'Business leads', group: 'Support' },

  { key: 'payouts', label: 'Instructor payouts', group: 'Finance' },
  { key: 'affiliatepayouts', label: 'Affiliate payouts', group: 'Finance' },

  { key: 'announce', label: 'Announcements', group: 'Marketing' },

  { key: 'settings', label: 'Settings', group: 'System' },
];

/** Instructor sidebar sections (Payment appended when source === application). */
export const INSTRUCTOR_SECTIONS: AdminSection[] = [
  { key: 'overview', label: 'Overview', group: 'General' },
  { key: 'reports', label: 'Reports', group: 'General' },

  { key: 'courses', label: 'Courses', group: 'Store' },
  { key: 'qbankadm', label: 'Question bank', group: 'Store' },
  { key: 'assignments', label: 'Assignments', group: 'Store' },

  { key: 'reviews', label: 'Reviews', group: 'Support' },

  { key: 'profile', label: 'Profile', group: 'Account' },
];

export const INSTRUCTOR_PAYMENT_SECTION: AdminSection = {
  key: 'payment',
  label: 'Payment',
  group: 'Finance',
};

/** Affiliate partner panel (mounted under /partner). */
export const AFFILIATE_SECTIONS: AdminSection[] = [
  { key: 'overview', label: 'Overview', group: 'General' },
  { key: 'afflink', label: 'My link', group: 'Growth' },
  { key: 'affcourses', label: 'Courses', group: 'Growth' },
  { key: 'affsales', label: 'Sales', group: 'Growth' },
  { key: 'payment', label: 'Payment', group: 'Finance' },
  { key: 'profile', label: 'Profile', group: 'Account' },
];

/** Order manager panel (mounted under /order-manager). */
export const ORDER_MANAGER_SECTIONS: AdminSection[] = [
  { key: 'overview', label: 'Overview', group: 'General' },
  { key: 'orders', label: 'Course orders', group: 'Orders' },
  { key: 'bookorders', label: 'Book orders', group: 'Orders' },
];

/** Student learning panel (mounted under /dashboard). */
export const STUDENT_SECTIONS: AdminSection[] = [
  { key: 'overview', label: 'Overview', group: 'General' },
  { key: 'purchased', label: 'Purchased', group: 'Learning' },
  { key: 'free', label: 'Free courses', group: 'Learning' },
  { key: 'completed', label: 'Completed', group: 'Learning' },
  { key: 'certs', label: 'Certificates', group: 'Learning' },
  { key: 'wishlist', label: 'Wishlist', group: 'Learning' },
  { key: 'orders', label: 'Orders', group: 'Commerce' },
  { key: 'support', label: 'Support', group: 'Account' },
  { key: 'settings', label: 'Settings', group: 'Account' },
];

const SUPPORT_KEYS = new Set([
  'overview',
  'tickets',
  'qa',
  'reviews',
  'counselling',
  'leads',
  'students',
]);

export interface SectionsForRoleOptions {
  /** When true, Finance → Payment is included for instructors. */
  includePayment?: boolean;
}

export function sectionsForRole(role: string, options?: SectionsForRoleOptions): AdminSection[] {
  if (role === 'support') {
    return ALL_ADMIN_SECTIONS.filter((s) => SUPPORT_KEYS.has(s.key));
  }
  if (role === 'instructor' || role === 'mentor') {
    const sections = [...INSTRUCTOR_SECTIONS];
    if (options?.includePayment) sections.push(INSTRUCTOR_PAYMENT_SECTION);
    return sections;
  }
  if (role === 'affiliate') {
    return [...AFFILIATE_SECTIONS];
  }
  if (role === 'order_manager') {
    return [...ORDER_MANAGER_SECTIONS];
  }
  if (role === 'admin' || role === 'superadmin') {
    return ALL_ADMIN_SECTIONS;
  }
  return [];
}

export function canAccessSection(role: string, section: string, options?: SectionsForRoleOptions): boolean {
  return sectionsForRole(role, options).some((s) => s.key === section);
}

export function groupAdminSections(sections: AdminSection[]): { group: string; items: AdminSection[] }[] {
  const byGroup = new Map<string, AdminSection[]>();
  for (const section of sections) {
    const group = section.group ?? 'Other';
    const list = byGroup.get(group) ?? [];
    list.push(section);
    byGroup.set(group, list);
  }
  const ordered: { group: string; items: AdminSection[] }[] = [];
  for (const group of ADMIN_NAV_GROUPS) {
    const items = byGroup.get(group);
    if (items?.length) ordered.push({ group, items });
  }
  for (const [group, items] of byGroup) {
    if (!(ADMIN_NAV_GROUPS as readonly string[]).includes(group) && items.length) {
      ordered.push({ group, items });
    }
  }
  return ordered;
}
